import React, { useEffect, useState } from 'react';
import { getUserById, getFollowersList, getFollowingList } from '../services/userService';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import FollowButton from './FollowButton';

const FollowersFollowingModal = ({ isOpen, onClose, userId, type, userData }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (!isOpen || !userId || !userData) {
            setUsers([]);
            setLoading(false);
            return;
        }

        const fetchUsers = async () => {
            setLoading(true);
            try {
                // Get the list of user IDs from subcollections (followers or following)
                const userIds = type === 'followers' 
                    ? await getFollowersList(userId)
                    : await getFollowingList(userId);

                if (!userIds || userIds.length === 0) {
                    setUsers([]);
                    setLoading(false);
                    return;
                }

                // Fetch user data for each ID
                const userPromises = userIds.map(uid => getUserById(uid));
                const userResults = await Promise.all(userPromises);
                
                // Filter out null results and add IDs
                const validUsers = userResults
                    .map((user, index) => user ? { ...user, id: userIds[index] } : null)
                    .filter(user => user !== null);

                setUsers(validUsers);
            } catch (err) {
                console.error('Error fetching users:', err);
                setUsers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [isOpen, userId, type, userData]);

    if (!isOpen) return null;

    const handleUserClick = (targetUserId) => {
        onClose();
        navigate(`/profile/${targetUserId}`);
    };

    const currentUid = auth.currentUser?.uid;

    return (
        <div 
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div 
                className="bg-[#141414] rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col border border-[#222] shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-[#333]">
                    <h2 className="text-lg font-semibold text-white">
                        {type === 'followers' ? 'Followers' : 'Following'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white/60 hover:text-white transition"
                    >
                        ✕
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-white/60">Loading...</div>
                        </div>
                    ) : users.length === 0 ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-white/60">
                                No {type === 'followers' ? 'followers' : 'following'} yet
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3 p-4">
                            {users.map((user) => {
                                const aboutRaw =
                                    typeof user.bio === 'string'
                                        ? user.bio
                                        : typeof user.about === 'string'
                                            ? user.about
                                            : '';
                                const about =
                                    aboutRaw.length > 140 ? `${aboutRaw.slice(0, 140)}…` : aboutRaw;

                                return (
                                    <div
                                        key={user.id}
                                        className="rounded-2xl bg-[#1c1c1c] border border-[#2a2a2a] hover:border-[#ff2a2a]/40 hover:bg-[#222] transition-all p-4 cursor-pointer shadow-md"
                                        onClick={() => handleUserClick(user.id)}
                                    >
                                        <div className="flex items-center gap-4">
                                            {/* Profile Photo */}
                                            <div className="w-16 h-16 rounded-full overflow-hidden bg-[#2a2a2a] flex items-center justify-center flex-shrink-0 ring-2 ring-[#ff2a2a]/20">
                                                {user.photoURL ? (
                                                    <img
                                                        src={user.photoURL}
                                                        alt={user.name || user.displayName}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="text-2xl font-bold text-white">
                                                        {(user.name || user.displayName || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                            </div>

                                            {/* User Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="text-lg font-semibold text-white truncate">
                                                        {user.name || user.displayName || user.fullName || 'Unknown User'}
                                                    </h3>
                                                    {user.role && (
                                                        <span className="px-2 py-1 rounded-full bg-[#ff2a2a] text-white text-xs font-semibold">
                                                            {user.role === 'artist' ? '🎤 Artist' : '🎧 Listener'}
                                                        </span>
                                                    )}
                                                </div>
                                                {user.email && (
                                                    <p className="text-sm text-white/60 truncate">{user.email}</p>
                                                )}
                                                {about && (
                                                    <p className="text-xs text-white/50 mt-1">
                                                        {about}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Actions */}
                                            <div
                                                onClick={(e) => e.stopPropagation()}
                                                className="flex flex-col items-end gap-2"
                                            >
                                                {currentUid === user.id ? (
                                                    <span className="text-xs text-white/40 uppercase tracking-wide">
                                                        You
                                                    </span>
                                                ) : currentUid ? (
                                                    <FollowButton targetUid={user.id} variant="compact" />
                                                ) : (
                                                    <button
                                                        className="px-4 py-2 rounded-full bg-[#ff2a2a] text-white text-sm font-semibold hover:bg-[#ff1111] transition"
                                                        onClick={() => {
                                                            onClose();
                                                            navigate('/login');
                                                        }}
                                                    >
                                                        Follow
                                                    </button>
                                                )}
                                                <button
                                                    className="px-4 py-2 rounded-full border border-white/10 text-white/80 text-sm font-medium hover:border-[#ff2a2a] hover:text-white transition"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUserClick(user.id);
                                                    }}
                                                >
                                                    View Profile
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FollowersFollowingModal;

